import { useState } from 'react';
import { BottomSheet, ConfirmDialog } from '@/shared/components';
import { AvailabilityCard } from './AvailabilityCard';
import { GuestSteps } from './GuestSteps';
import { ManualBookingAfter } from './ManualBookingAfter';
import { ManualBookingFooter } from './ManualBookingFooter';
import { ManualBookingSummary } from './ManualBookingSummary';
import { StayStep } from './StayStep';
import { useManualBookingForm, type PanelApartment } from './useManualBookingForm';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Festes Apartment (Apartmentseite, Kalender) – oder die Auswahl, aus der gewählt wird (Liste). */
  apartment: PanelApartment;
  prefillFrom?: string;
  prefillTo?: string;
}

/**
 * Manuelle Buchung als Panel (Board „Buchungen · Manuelle Buchung“): 600 px rechts, am Handy
 * Vollbild. Drei Schritte, Verfügbarkeitsprüfung, was danach passiert, Summe im festen Fuß.
 * Für Gäste, die direkt buchen – am Telefon, per Mail oder vor Ort.
 */
export function ManualBookingPanel({
  open,
  onOpenChange,
  apartment,
  prefillFrom,
  prefillTo,
}: Props) {
  const form = useManualBookingForm({
    open,
    apartment,
    prefillFrom,
    prefillTo,
    onCreated: () => onOpenChange(false),
  });
  const [leaving, setLeaving] = useState(false);
  // Angefangene Eingaben gehen weder durch Escape noch durch einen Klick daneben verloren (§8).
  const requestClose = () => {
    if (form.create.isPending) return;
    if (form.dirty) setLeaving(true);
    else onOpenChange(false);
  };
  const name = form.property
    ? (form.property.adminDisplayName ?? form.property.name)
    : 'Apartment wählen';
  const currency = form.property?.currency ?? null;
  return (
    <>
      <BottomSheet
        open={open}
        onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}
        title="Manuelle Buchung"
        description="Für Gäste, die direkt buchen – am Telefon, per Mail oder vor Ort."
        desktop="panel"
        className="md:rounded-sheet h-[100dvh] max-h-[100dvh] rounded-none md:h-auto md:w-[600px]"
        footer={
          <ManualBookingFooter
            nights={form.datesValid ? form.nights : 0}
            rate={form.rate}
            currency={currency}
            error={form.create.error}
            pending={form.create.isPending}
            canSubmit={form.canSubmit}
            onCancel={requestClose}
            onSubmit={form.submit}
          />
        }
      >
        <div className="flex flex-col gap-5 pb-2">
          <ManualBookingSummary
            name={name}
            from={form.from}
            to={form.to}
            nights={form.datesValid ? form.nights : 0}
            adults={form.guest.adults}
            state={form.property && form.datesValid ? form.state : null}
          />
          <StayStep
            choices={form.choices}
            propertyId={form.propertyId}
            apartmentName={name}
            onPropertyChange={form.setChosenId}
            from={form.from}
            to={form.to}
            onFromChange={form.setFrom}
            onToChange={form.setTo}
            guest={form.guest}
            onGuestChange={form.setField}
          />
          <GuestSteps guest={form.guest} onChange={form.setField} currency={currency} />
          {form.property && form.datesValid && (
            <AvailabilityCard
              state={form.state}
              nights={form.nights}
              hardConflicts={form.avail.hardConflicts}
              softConflicts={form.avail.softConflicts}
              override={form.override}
              onOverrideChange={form.setOverride}
              onRetry={form.avail.retry}
              error={form.avail.error}
              offline={form.avail.isOffline}
            />
          )}
          <ManualBookingAfter />
        </div>
      </BottomSheet>
      <ConfirmDialog
        open={leaving}
        onOpenChange={setLeaving}
        title="Eingaben verwerfen?"
        description="Die Buchung ist noch nicht angelegt. Beim Schließen gehen die Eingaben verloren."
        confirmLabel="Verwerfen"
        cancelLabel="Weiter bearbeiten"
        onConfirm={() => {
          setLeaving(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
